/**
 * ValidationSummary Component
 *
 * Displays a summary of all validation errors at the form level
 */

import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ValidationError } from "utils/validation/calculationValidation";

interface ValidationSummaryProps {
  /**
   * All validation errors
   */
  errors: ValidationError[];

  /**
   * Title for the summary
   */
  title?: string;

  /**
   * Callback when error is clicked (for navigation)
   */
  onErrorClick?: (error: ValidationError) => void;

  /**
   * Show warnings in addition to errors
   */
  showWarnings?: boolean;
}

const SummaryContainer = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SummaryTitle = styled.h4`
  color: #991b1b;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErrorItem = styled.li<{
  clickable?: boolean;
  severity?: "error" | "warning";
}>`
  color: ${(props) => (props.severity === "warning" ? "#92400e" : "#991b1b")};
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.clickable ? "#fee2e2" : "transparent"};
  }
`;

const ErrorIcon = styled.span<{ severity?: "error" | "warning" }>`
  color: ${(props) => (props.severity === "warning" ? "#f59e0b" : "#dc2626")};
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ErrorMessage = styled.span`
  flex: 1;
`;

const ErrorField = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

/**
 * Display a summary of all validation errors
 */
export const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  errors,
  title = "Please correct the following errors:",
  onErrorClick,
  showWarnings = true,
}) => {
  const { t } = useTranslation();

  // Filter errors based on showWarnings
  const displayErrors = showWarnings
    ? errors
    : errors.filter((e) => e.severity === "error");

  if (displayErrors.length === 0) return null;

  const handleErrorClick = (error: ValidationError) => {
    if (onErrorClick) {
      onErrorClick(error);
    }
  };

  return (
    <SummaryContainer role="alert" aria-live="assertive">
      <SummaryTitle>
        <ErrorIcon severity="error">⚠</ErrorIcon>
        {title} ({displayErrors.length})
      </SummaryTitle>
      <ErrorList>
        {displayErrors.map((error, index) => (
          <ErrorItem
            key={`${error.field}-${index}`}
            clickable={!!onErrorClick}
            severity={error.severity}
            onClick={() => handleErrorClick(error)}
          >
            <ErrorIcon severity={error.severity}>
              {error.severity === "warning" ? "⚠" : "✕"}
            </ErrorIcon>
            <ErrorMessage>
              <ErrorField>{formatFieldName(error.field)}:</ErrorField>
              {t(error.message, error.message)}
            </ErrorMessage>
          </ErrorItem>
        ))}
      </ErrorList>
    </SummaryContainer>
  );
};

/**
 * Format field path for display
 */
function formatFieldName(fieldPath: string): string {
  return fieldPath
    .split(".")
    .map((part) => {
      // Convert camelCase to Title Case
      return part
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    })
    .join(" → ");
}

export default ValidationSummary;
